import { useEffect, useState } from 'react';
import memoize from 'memoizee';
import { provider } from '../../helpers/client';

interface SourceItem {
    value: string;
    name: string;
}

const carModelsCached = memoize((brandId: string) => provider.carModels({ brandId }), {
    promise: true,
    maxAge: 1000 * 60 * 5,
});

const carModelsYearsCached = memoize((modelId: string) => provider.carModelYears({ modelId }), {
    promise: true,
    maxAge: 1000 * 60 * 5,
});

const carPowersCached = memoize(
    (brandId: string, modelId: string, yearFrom: string) => provider.carModelPowers({ brandId, modelId, yearFrom }),
    {
        promise: true,
        maxAge: 1000 * 60 * 5,
    },
);

export const useCarModels = (autoBrandId?: string, autoModelId?: string, autoYear?: string) => {
    const [modelsIsLoading, setModelsIsLoading] = useState(false);
    const [yearsIsLoading, setYearsIsLoading] = useState(false);
    const [powersIsLoading, setPowersIsLoading] = useState(false);

    const [autoModelsOptions, setAutoModelsOptions] = useState<Array<SourceItem>>([]);
    const [autoYearsOptions, setAutoYearsOptions] = useState<Array<SourceItem>>([]);
    const [autoPowersOptions, setAutoPowersOptions] = useState<Array<SourceItem>>([]);

    const loadModelSource = async (brandId: string) => {
        setModelsIsLoading(true);
        try {
            const sources = (await carModelsCached(brandId)).map(({ name, id: value }) => ({
                name,
                value: value.toString(),
            }));
            setAutoModelsOptions(sources);
        } finally {
            setModelsIsLoading(false);
        }
    };

    useEffect(() => {
        if (autoBrandId) {
            loadModelSource(autoBrandId);
        }
    }, [autoBrandId]);

    const loadYearSource = async (modelId: string) => {
        setYearsIsLoading(true);
        try {
            const sources = (await carModelsYearsCached(modelId)).map((value) => ({
                name: value.toString(),
                value: value.toString(),
            }));
            setAutoYearsOptions(sources);
        } finally {
            setYearsIsLoading(false);
        }
    };

    useEffect(() => {
        if (autoModelId) {
            loadYearSource(autoModelId);
        }
    }, [autoModelId]);

    const loadPowersSource = async (brandId: string, modelId: string, yearFrom: string) => {
        setPowersIsLoading(true);
        try {
            const sources = (await carPowersCached(brandId, modelId, yearFrom)).map((value) => ({
                name: `${value} л.с.`,
                value: value.toString(),
            }));
            setAutoPowersOptions(sources);
        } finally {
            setPowersIsLoading(false);
        }
    };

    useEffect(() => {
        if (autoBrandId && autoModelId && autoYear) {
            loadPowersSource(autoBrandId, autoModelId, autoYear);
        }
    }, [autoBrandId, autoModelId, autoYear]);

    return { modelsIsLoading, autoModelsOptions, yearsIsLoading, autoYearsOptions, powersIsLoading, autoPowersOptions };
};
